import React, { useMemo, useState } from 'react';
import { DownloadIcon, CheckCircle2Icon, CheckIcon, ShieldCheckIcon, CircleIcon } from 'lucide-react';
import { CodeBlock } from '../components/ui/CodeBlock';
import { ObjectIdPill } from '../components/ui/ObjectIdPill';
import { getWizardState, getRecommendation, generateEnvVars, generateWebhookHandler, generateDbSchema, generateBackendEndpoints } from '../lib/wizardUtils';
const TABS = ['Overview', 'Environment Variables', 'Stripe Objects', 'Webhooks', 'Database Model', 'Backend Endpoints', 'Frontend Guide', 'Test Checklist', 'Go-Live Checklist'];
export function ImplementationPackPage() {
  const [activeTab, setActiveTab] = useState('Overview');
  const wizardState = useMemo(() => getWizardState(), []);
  const rec = useMemo(() => getRecommendation(wizardState), [wizardState]);
  const envVars = useMemo(() => generateEnvVars(wizardState, rec), [wizardState, rec]);
  const webhookHandler = useMemo(() => generateWebhookHandler(wizardState, rec), [wizardState, rec]);
  const dbSchema = useMemo(() => generateDbSchema(wizardState, rec), [wizardState, rec]);
  const endpoints = useMemo(() => generateBackendEndpoints(wizardState, rec), [wizardState, rec]);
  const [testChecks, setTestChecks] = useState<Record<string, boolean>>({});
  const [goLiveChecks, setGoLiveChecks] = useState<Record<string, boolean>>({});
  const products = rec.stripeObjects.filter(o => o.type === 'product');
  const prices = rec.stripeObjects.filter(o => o.type === 'price');
  const webhookObj = rec.stripeObjects.find(o => o.type === 'webhook');
  const portalObj = rec.stripeObjects.find(o => o.type === 'portal');
  const testItems = useMemo(() => {
    const items = ['Install Stripe CLI and run stripe listen --forward-to localhost:3000/api/webhook', 'Create a test customer using Stripe test mode', `Complete a test ${wizardState.chargeModel === 'Subscription' ? 'subscription' : 'payment'} via Checkout`, 'Verify webhook events are received and processed', 'Check that your database is updated correctly after payment'];
    if (rec.modules.some(m => m.name === 'Customer Portal')) {
      items.push('Test Customer Portal: update payment method, cancel subscription');
    }
    if (wizardState.features.coupons) {
      items.push('Test applying a coupon code during checkout');
    }
    if (wizardState.features.trials) {
      items.push('Test free trial signup and verify trial_end webhook');
    }
    items.push('Test failed payment handling (use card 4000000000000341)');
    return items;
  }, [wizardState, rec]);
  const goLiveItems = useMemo(() => {
    const items = ['Switch from Stripe test keys to live keys', 'Update STRIPE_WEBHOOK_SECRET with the live webhook secret', 'Verify all environment variables are set in production', 'Create live products and prices (or use Stripe dashboard to copy from test)', 'Set up the live webhook endpoint in Stripe Dashboard', 'Test a real payment with a small amount', 'Enable HTTPS on your webhook endpoint'];
    if (wizardState.features.tax) {
      items.push('Register for Stripe Tax and configure tax settings');
    }
    if (rec.modules.some(m => m.name === 'Customer Portal')) {
      items.push('Configure Customer Portal branding in Stripe Dashboard');
    }
    items.push('Set up error monitoring (Sentry, LogRocket, etc.)', 'Enable Stripe Radar for fraud protection');
    return items;
  }, [wizardState, rec]);
  return <div className="flex flex-col h-full" data-id="element-677">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-678">
        <div data-id="element-679">
          <h1 className="text-3xl font-bold mb-1" data-id="element-680">Your Implementation Pack</h1>
          <p className="text-muted-foreground" data-id="element-681">
            Tailored for {rec.frontendLabel} + {rec.backendLabel} +{' '}
            {rec.dbLabel}
          </p>
        </div>
        <button className="bg-background border border-border hover:bg-muted text-foreground px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 shadow-sm" data-id="element-682">
          <DownloadIcon className="w-4 h-4" data-id="element-683" />
          Download All (.zip)
        </button>
      </div>

      <div className="flex-1 flex flex-col md:flex-row gap-8" data-id="element-684">
        {/* Tabs Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0" data-id="element-685">
          <nav className="flex flex-row md:flex-col overflow-x-auto md:overflow-visible gap-1 pb-2 md:pb-0" data-id="element-686">
            {TABS.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`text-left px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} data-id="element-687">
                {tab}
              </button>)}
          </nav>

          <div className="hidden md:block mt-8 bg-primary/5 border border-primary/10 rounded-lg p-4" data-id="element-688">
            <h4 className="font-semibold mb-2 flex items-center gap-2" data-id="element-689">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-690" />
              Enable Monitoring
            </h4>
            <p className="text-xs text-muted-foreground mb-3" data-id="element-691">
              Get alerted if your webhooks fail or configuration drifts.
            </p>
            <button className="w-full bg-primary text-primary-foreground text-xs font-medium py-2 rounded hover:opacity-90 transition-opacity" data-id="element-692">
              Add for $19/mo
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-card border border-border rounded-xl shadow-sm overflow-hidden flex flex-col min-h-[500px]" data-id="element-693">
          {/* Overview */}
          {activeTab === 'Overview' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-694">
              <h2 className="text-2xl font-bold mb-6" data-id="element-695">Setup Complete</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-696">
                <div className="border border-border rounded-lg p-4 bg-pk-surface" data-id="element-697">
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-698">
                    {products.length}
                  </div>
                  <div className="text-sm font-medium text-muted-foreground" data-id="element-699">
                    Products Created
                  </div>
                </div>
                <div className="border border-border rounded-lg p-4 bg-pk-surface" data-id="element-700">
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-701">
                    {prices.length}
                  </div>
                  <div className="text-sm font-medium text-muted-foreground" data-id="element-702">
                    Prices Configured
                  </div>
                </div>
                <div className="border border-border rounded-lg p-4 bg-pk-surface" data-id="element-703">
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-704">1</div>
                  <div className="text-sm font-medium text-muted-foreground" data-id="element-705">
                    Webhook Endpoint
                  </div>
                </div>
                <div className="border border-border rounded-lg p-4 bg-pk-surface" data-id="element-706">
                  <div className="text-2xl font-bold text-primary mb-1" data-id="element-707">
                    {rec.events.length}
                  </div>
                  <div className="text-sm font-medium text-muted-foreground" data-id="element-708">
                    Events Subscribed
                  </div>
                </div>
              </div>

              <div className="mb-8 p-4 bg-muted/30 border border-border rounded-lg" data-id="element-709">
                <h3 className="font-semibold mb-2" data-id="element-710">Your Configuration</h3>
                <div className="grid sm:grid-cols-2 gap-2 text-sm" data-id="element-711">
                  <div data-id="element-712">
                    <span className="text-muted-foreground" data-id="element-713">Product type:</span>{' '}
                    <span className="font-medium" data-id="element-714">
                      {wizardState.buildingType}
                    </span>
                  </div>
                  <div data-id="element-715">
                    <span className="text-muted-foreground" data-id="element-716">
                      Pricing model:
                    </span>{' '}
                    <span className="font-medium" data-id="element-717">
                      {wizardState.chargeModel}
                    </span>
                  </div>
                  <div data-id="element-718">
                    <span className="text-muted-foreground" data-id="element-719">Stack:</span>{' '}
                    <span className="font-medium" data-id="element-720">
                      {rec.frontendLabel} + {rec.backendLabel}
                    </span>
                  </div>
                  <div data-id="element-721">
                    <span className="text-muted-foreground" data-id="element-722">Database:</span>{' '}
                    <span className="font-medium" data-id="element-723">{rec.dbLabel}</span>
                  </div>
                </div>
              </div>

              <h3 className="font-semibold text-lg mb-4" data-id="element-724">Next Steps</h3>
              <ul className="space-y-3" data-id="element-725">
                <li className="flex items-start gap-3" data-id="element-726">
                  <CheckCircle2Icon className="w-5 h-5 text-muted-foreground shrink-0" data-id="element-727" />
                  <span data-id="element-728">
                    Add Environment Variables to your {rec.frontendLabel}{' '}
                    project
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-729">
                  <CheckCircle2Icon className="w-5 h-5 text-muted-foreground shrink-0" data-id="element-730" />
                  <span data-id="element-731">Run the {rec.dbLabel} schema migration</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-732">
                  <CheckCircle2Icon className="w-5 h-5 text-muted-foreground shrink-0" data-id="element-733" />
                  <span data-id="element-734">
                    Implement the webhook handler in {rec.backendLabel}
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-735">
                  <CheckCircle2Icon className="w-5 h-5 text-muted-foreground shrink-0" data-id="element-736" />
                  <span data-id="element-737">
                    Add checkout and{' '}
                    {rec.modules.some(m => m.name === 'Customer Portal') ? 'portal ' : ''}
                    buttons to your frontend
                  </span>
                </li>
              </ul>
            </div>}

          {/* Environment Variables */}
          {activeTab === 'Environment Variables' && <div className="p-6 md:p-8 animate-slide-in flex flex-col h-full" data-id="element-738">
              <h2 className="text-2xl font-bold mb-2" data-id="element-739">Environment Variables</h2>
              <p className="text-muted-foreground mb-6" data-id="element-740">
                Add these to your{' '}
                <code className="bg-muted px-1 py-0.5 rounded" data-id="element-741">
                  {wizardState.stack.frontend === 'Next.js' ? '.env.local' : '.env'}
                </code>{' '}
                file.
              </p>
              <div className="flex-1" data-id="element-742">
                <CodeBlock language="env" code={envVars} className="h-full" data-id="element-743" />
              </div>
            </div>}

          {/* Stripe Objects */}
          {activeTab === 'Stripe Objects' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-744">
              <h2 className="text-2xl font-bold mb-6" data-id="element-745">Stripe Objects</h2>
              <div className="space-y-6" data-id="element-746">
                {products.map(product => {
              const productPrices = prices.filter(p => p.name.toLowerCase().includes(product.name.split(' ')[0].toLowerCase()));
              return <div key={product.id} className="border border-border rounded-lg p-5" data-id="element-747">
                      <div className="flex justify-between items-start mb-4" data-id="element-748">
                        <div data-id="element-749">
                          <h3 className="font-bold text-lg" data-id="element-750">{product.name}</h3>
                          <p className="text-sm text-muted-foreground" data-id="element-751">
                            Product ID: <ObjectIdPill id={product.id} data-id="element-752" />
                          </p>
                        </div>
                        {product.details && <span className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground" data-id="element-753">
                            {product.details}
                          </span>}
                      </div>
                      {productPrices.length > 0 && <div className="space-y-2" data-id="element-754">
                          {productPrices.map(price => <div key={price.id} className="flex justify-between items-center p-2 bg-pk-surface rounded text-sm" data-id="element-755">
                              <span data-id="element-756">{price.name}</span>
                              <ObjectIdPill id={price.id} data-id="element-757" />
                            </div>)}
                        </div>}
                    </div>;
            })}

                {webhookObj && <div className="border border-border rounded-lg p-5" data-id="element-758">
                    <h3 className="font-bold text-lg" data-id="element-759">Webhook Endpoint</h3>
                    <p className="text-sm text-muted-foreground mb-2" data-id="element-760">
                      ID: <ObjectIdPill id={webhookObj.id} data-id="element-761" />
                    </p>
                    <p className="text-sm text-muted-foreground" data-id="element-762">
                      {webhookObj.details}
                    </p>
                  </div>}

                {portalObj && <div className="border border-border rounded-lg p-5" data-id="element-763">
                    <h3 className="font-bold text-lg" data-id="element-764">
                      Customer Portal Configuration
                    </h3>
                    <p className="text-sm text-muted-foreground" data-id="element-765">
                      ID: <ObjectIdPill id={portalObj.id} data-id="element-766" />
                    </p>
                  </div>}
              </div>
            </div>}

          {/* Webhooks */}
          {activeTab === 'Webhooks' && <div className="p-6 md:p-8 animate-slide-in flex flex-col h-full" data-id="element-767">
              <h2 className="text-2xl font-bold mb-2" data-id="element-768">Webhook Handler</h2>
              <p className="text-muted-foreground mb-4" data-id="element-769">
                {rec.events.length} events configured for your{' '}
                {rec.backendLabel} backend.
              </p>
              <div className="mb-4" data-id="element-770">
                <h3 className="font-semibold text-sm mb-2" data-id="element-771">
                  Subscribed Events
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-772">
                  {rec.events.map(e => <span key={e} className="text-xs font-mono bg-muted px-2 py-1 rounded" data-id="element-773">
                      {e}
                    </span>)}
                </div>
              </div>
              <div className="flex-1" data-id="element-774">
                <CodeBlock language={wizardState.stack.backend === 'Python' ? 'python' : 'typescript'} code={webhookHandler} data-id="element-775" />
              </div>
            </div>}

          {/* Database Model */}
          {activeTab === 'Database Model' && <div className="p-6 md:p-8 animate-slide-in flex flex-col h-full" data-id="element-776">
              <h2 className="text-2xl font-bold mb-2" data-id="element-777">Database Schema</h2>
              <p className="text-muted-foreground mb-6" data-id="element-778">
                {rec.tables.length} tables for {rec.dbLabel}.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6" data-id="element-779">
                {rec.tables.map(table => <div key={table.name} className="border border-border rounded-lg p-4" data-id="element-780">
                    <div className="font-mono text-sm font-semibold mb-2 text-primary" data-id="element-781">
                      {table.name}
                    </div>
                    <div className="space-y-1" data-id="element-782">
                      {table.columns.map(col => <div key={col} className="text-xs text-muted-foreground font-mono" data-id="element-783">
                          {col}
                        </div>)}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground" data-id="element-784">
                      Stripe ref: {table.stripeRef}
                    </div>
                  </div>)}
              </div>

              <div className="flex-1" data-id="element-785">
                <CodeBlock language={['MongoDB', 'Firebase'].includes(wizardState.stack.database) ? 'javascript' : 'sql'} code={dbSchema} data-id="element-786" />
              </div>
            </div>}

          {/* Backend Endpoints */}
          {activeTab === 'Backend Endpoints' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-787">
              <h2 className="text-2xl font-bold mb-2" data-id="element-788">API Endpoints</h2>
              <p className="text-muted-foreground mb-6" data-id="element-789">
                {endpoints.length} endpoints for your {rec.backendLabel}{' '}
                backend.
              </p>
              <div className="space-y-6" data-id="element-790">
                {endpoints.map((ep, i) => <div key={i} className="border border-border rounded-lg overflow-hidden" data-id="element-791">
                    <div className="flex items-center gap-3 p-4 border-b border-border bg-muted/30" data-id="element-792">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${ep.method === 'POST' ? 'bg-pk-success/10 text-pk-success' : 'bg-primary/10 text-primary'}`} data-id="element-793">
                        {ep.method}
                      </span>
                      <span className="font-mono text-sm font-medium" data-id="element-794">
                        {ep.path}
                      </span>
                      <span className="text-sm text-muted-foreground ml-auto hidden sm:block" data-id="element-795">
                        {ep.description}
                      </span>
                    </div>
                    <CodeBlock language={wizardState.stack.backend === 'Python' ? 'python' : 'typescript'} code={ep.code} data-id="element-796" />
                  </div>)}
              </div>
            </div>}

          {/* Frontend Guide */}
          {activeTab === 'Frontend Guide' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-797">
              <h2 className="text-2xl font-bold mb-2" data-id="element-798">Frontend Integration</h2>
              <p className="text-muted-foreground mb-6" data-id="element-799">
                How to integrate Stripe into your {rec.frontendLabel} frontend.
              </p>

              <div className="space-y-6" data-id="element-800">
                <div data-id="element-801">
                  <h3 className="font-semibold mb-3" data-id="element-802">1. Install Stripe.js</h3>
                  <CodeBlock language="bash" code={`npm install @stripe/stripe-js${rec.frontendLabel === 'React' || rec.frontendLabel === 'Next.js' ? ' @stripe/react-stripe-js' : ''}`} data-id="element-803" />
                </div>

                <div data-id="element-804">
                  <h3 className="font-semibold mb-3" data-id="element-805">
                    2. Create Checkout Button
                  </h3>
                  <CodeBlock language="typescript" code={`import { loadStripe } from '@stripe/stripe-js'

const stripePromise = loadStripe(process.env.${wizardState.stack.frontend === 'Next.js' ? 'NEXT_PUBLIC_' : ''}STRIPE_PUBLISHABLE_KEY!)

export function CheckoutButton({ priceId }: { priceId: string }) {
  const handleCheckout = async () => {
    const res = await fetch('${wizardState.stack.frontend === 'Next.js' ? '/api/checkout' : '/checkout'}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceId }),
    })
    const { url } = await res.json()
    window.location.href = url
  }

  return (
    <button onClick={handleCheckout}>
      ${wizardState.chargeModel === 'Subscription' ? 'Subscribe Now' : 'Buy Now'}
    </button>
  )
}`} data-id="element-806" />
                </div>

                {rec.modules.some(m => m.name === 'Customer Portal') && <div data-id="element-807">
                    <h3 className="font-semibold mb-3" data-id="element-808">
                      3. Customer Portal Button
                    </h3>
                    <CodeBlock language="typescript" code={`export function ManageBillingButton({ customerId }: { customerId: string }) {
  const handlePortal = async () => {
    const res = await fetch('${wizardState.stack.frontend === 'Next.js' ? '/api/portal' : '/portal'}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId }),
    })
    const { url } = await res.json()
    window.location.href = url
  }

  return (
    <button onClick={handlePortal}>
      Manage Billing
    </button>
  )
}`} data-id="element-809" />
                  </div>}
              </div>
            </div>}

          {/* Test Checklist */}
          {activeTab === 'Test Checklist' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-810">
              <h2 className="text-2xl font-bold mb-2" data-id="element-811">Test Checklist</h2>
              <p className="text-muted-foreground mb-6" data-id="element-812">
                Verify everything works in Stripe test mode before going live.
              </p>
              <div className="space-y-3" data-id="element-813">
                {testItems.map((item, i) => {
              const key = `test-${i}`;
              const checked = testChecks[key] || false;
              return <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-pk-success/5 border-pk-success/20' : 'border-border hover:bg-muted/30'}`} data-id="element-814">
                      <button onClick={() => setTestChecks(prev => ({
                  ...prev,
                  [key]: !checked
                }))} className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-pk-success border-pk-success text-white' : 'border-border'}`} data-id="element-815">
                        {checked && <CheckIcon className="w-3 h-3" data-id="element-816" />}
                      </button>
                      <span className={`text-sm ${checked ? 'line-through text-muted-foreground' : ''}`} data-id="element-817">
                        {item}
                      </span>
                    </label>;
            })}
              </div>
              <div className="mt-4 text-sm text-muted-foreground" data-id="element-818">
                {Object.values(testChecks).filter(Boolean).length} of{' '}
                {testItems.length} complete
              </div>
            </div>}

          {/* Go-Live Checklist */}
          {activeTab === 'Go-Live Checklist' && <div className="p-6 md:p-8 animate-slide-in" data-id="element-819">
              <h2 className="text-2xl font-bold mb-2" data-id="element-820">Go-Live Checklist</h2>
              <p className="text-muted-foreground mb-6" data-id="element-821">
                Final steps before accepting real payments.
              </p>
              <div className="space-y-3" data-id="element-822">
                {goLiveItems.map((item, i) => {
              const key = `live-${i}`;
              const checked = goLiveChecks[key] || false;
              return <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-pk-success/5 border-pk-success/20' : 'border-border hover:bg-muted/30'}`} data-id="element-823">
                      <button onClick={() => setGoLiveChecks(prev => ({
                  ...prev,
                  [key]: !checked
                }))} className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-pk-success border-pk-success text-white' : 'border-border'}`} data-id="element-824">
                        {checked && <CheckIcon className="w-3 h-3" data-id="element-825" />}
                      </button>
                      <span className={`text-sm ${checked ? 'line-through text-muted-foreground' : ''}`} data-id="element-826">
                        {item}
                      </span>
                    </label>;
            })}
              </div>
              <div className="mt-4 text-sm text-muted-foreground" data-id="element-827">
                {Object.values(goLiveChecks).filter(Boolean).length} of{' '}
                {goLiveItems.length} complete
              </div>
            </div>}
        </div>
      </div>
    </div>;
}